// src/features/owner/sections/content/PostsSection.tsx
import { useState } from 'react';
import styled from 'styled-components';
import { useOwnerList } from '@/hooks';
import { useSession } from '@/hooks/useSession';
import { ownerService } from '@/core/services/ownerService';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { OwnerPanel, DataTable } from '../../components';
import { ConfirmActionModal } from '../../components/ConfirmActionModal';
import type { Column } from '../../components';
import type { OwnerPostRow } from '@/core/models';
import type { ModerationCategory } from '@/core/models';

// ─── Styled primitives ────────────────────────────────────────────────────────

const RemovedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => `${theme.color.danger}22`};
  border: 1px solid transparent;
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function PostsSection() {
  const { data, loading, error, refetch } = useOwnerList(() => ownerService.getPosts());
  const action = useOwnerAction();
  const { isSuperuser } = useSession();

  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  async function handleRemove(postId: string, category?: ModerationCategory, note?: string) {
    if (!category) return;
    await action.removePost(postId, category, note);
    refetch();
  }

  async function handleRestore(postId: string) {
    await action.restorePost(postId);
    refetch();
  }

  const columns: Column<OwnerPostRow>[] = [
    {
      key: 'author',
      header: 'Author',
      sortBy: (r) => r.author,
      render: (r) => r.author,
    },
    {
      key: 'excerpt',
      header: 'Excerpt',
      render: (r) => r.excerpt,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.removed ? (
          <RemovedBadge>
            <Icon name="ban" size={12} />
            Removed
          </RemovedBadge>
        ) : null,
    },
    {
      key: 'createdAt',
      header: 'Posted',
      sortBy: (r) => r.createdAt ?? '',
      render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'Unknown'),
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        isSuperuser ? (
          <ActionCell>
            {r.removed ? (
              <Button
                variant="ghost"
                onClick={() => { void handleRestore(r.id); }}
              >
                Restore
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setPendingRemove(r.id)}
              >
                Remove
              </Button>
            )}
          </ActionCell>
        ) : null,
    },
  ];

  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;

  return (
    <OwnerPanel title="Posts">
      <DataTable<OwnerPostRow>
        columns={columns}
        rows={data?.results ?? []}
        rowKey={(r) => r.id}
        emptyIcon="file-text"
        emptyMessage={error ?? 'No posts yet'}
      />

      <ConfirmActionModal
        open={pendingRemove !== null}
        title="Remove Post"
        body={`Remove post ${pendingRemove ?? ''}? This is a soft removal: the post is hidden from public view but not deleted.`}
        confirmLabel="Remove"
        actionKind="remove-post"
        onConfirm={(category, note) => {
          if (pendingRemove) void handleRemove(pendingRemove, category, note);
          setPendingRemove(null);
        }}
        onClose={() => setPendingRemove(null)}
      />
    </OwnerPanel>
  );
}
